import axiosInstance from '../config/axios.config';

/**
 * MessagingService - Direct Messaging API Service
 * Handles all messaging-related API calls for LeadFlow V2.0 Module 8: Messaging
 */
const BASE_URL = '/leadflow/messaging';

const messagingService = {
    /**
     * Get list of conversations with last message preview
     * @param {Object} params - { per_page }
     * @returns {Promise} Conversations list
     */
    getConversations: async (params = {}) => {
        try {
            const response = await axiosInstance.get(`${BASE_URL}/conversations`, { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching conversations:', error);
            return { success: false, message: error.response?.data?.message || 'Failed to load conversations' };
        }
    },

    /**
     * Start new conversation with a connected user
     * @param {number} receiverProfileId - Profile ID of the connection
     * @returns {Promise} Created conversation
     */
    createConversation: async (receiverProfileId) => {
        try {
            const response = await axiosInstance.post(`${BASE_URL}/conversations`, {
                receiver_profile_id: receiverProfileId,
            });
            return response.data;
        } catch (error) {
            console.error('Error creating conversation:', error);
            return { success: false, message: error.response?.data?.message || 'Failed to create conversation' };
        }
    },

    /**
     * Get messages for a conversation (paginated)
     * @param {number} conversationId - Conversation ID
     * @param {Object} params - { per_page, page }
     * @returns {Promise} Messages list
     */
    getMessages: async (conversationId, params = {}) => {
        try {
            const response = await axiosInstance.get(`${BASE_URL}/conversations/${conversationId}/messages`, { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching messages:', error);
            return { success: false, message: error.response?.data?.message || 'Failed to load messages' };
        }
    },

    /**
     * Send a message in a conversation (text and/or file attachment)
     * @param {number} conversationId - Conversation ID
     * @param {string} body - Message text (optional if file provided)
     * @param {File|null} file - File attachment (PDF, CSV, TXT, XLSX, XLS - max 5MB)
     * @returns {Promise} Created message
     */
    sendMessage: async (conversationId, body, file = null) => {
        try {
            let response;
            if (file) {
                const formData = new FormData();
                if (body) formData.append('body', body);
                formData.append('file', file);
                response = await axiosInstance.post(
                    `${BASE_URL}/conversations/${conversationId}/messages`,
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );
            } else {
                response = await axiosInstance.post(`${BASE_URL}/conversations/${conversationId}/messages`, { body });
            }
            return response.data;
        } catch (error) {
            console.error('Error sending message:', error);
            return { success: false, message: error.response?.data?.message || 'Failed to send message' };
        }
    },

    /**
     * Mark all messages in a conversation as read
     * @param {number} conversationId - Conversation ID
     * @returns {Promise}
     */
    markAsRead: async (conversationId) => {
        try {
            const response = await axiosInstance.post(`${BASE_URL}/conversations/${conversationId}/read`);
            return response.data;
        } catch (error) {
            console.error('Error marking as read:', error);
            return { success: false, message: error.response?.data?.message || 'Failed to mark as read' };
        }
    },

    /**
     * Get total unread message count (for header badge)
     * @returns {Promise} { unread_count }
     */
    getUnreadCount: async () => {
        try {
            const response = await axiosInstance.get(`${BASE_URL}/conversations/unread-count`);
            return response.data;
        } catch (error) {
            console.error('Error fetching unread count:', error);
            return { success: false, data: { unread_count: 0 } };
        }
    },

    /**
     * Delete a conversation and all its messages
     * @param {number} conversationId - Conversation ID
     * @returns {Promise}
     */
    deleteConversation: async (conversationId) => {
        try {
            const response = await axiosInstance.delete(`${BASE_URL}/conversations/${conversationId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting conversation:', error);
            return { success: false, message: error.response?.data?.message || 'Failed to delete conversation' };
        }
    },
};

export default messagingService;
